import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  FileText,
  UserCheck,
  FolderOpen,
  Bot,
  Bell,
  Settings,
  Wallet,
  Building2,
  BarChart3,
  Link2,
  LogOut,
  Sparkles
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { triggerHaptic } from "@/hooks/useHaptic";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface MoreMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const menuItems = [
  { icon: Sparkles, label: "Central de IA", href: "/ia", color: "text-violet-500" },
  { icon: Users, label: "Clientes", href: "/clientes", color: "text-blue-500" },
  { icon: FileText, label: "Propostas", href: "/propostas", color: "text-emerald-500" },
  { icon: UserCheck, label: "Equipe", href: "/equipe", color: "text-purple-500" },
  { icon: FolderOpen, label: "Documentos", href: "/documentos", color: "text-amber-500" },
  { icon: Bot, label: "Chat IA", href: "/agente-ia", color: "text-pink-500" },
  { icon: Bell, label: "Alertas", href: "/alertas", color: "text-red-500" },
  { icon: Wallet, label: "Assinatura", href: "/assinatura", color: "text-indigo-500" },
  { icon: BarChart3, label: "Relatórios", href: "/relatorios", color: "text-cyan-500" },
  { icon: Link2, label: "Integrações", href: "/integracoes", color: "text-orange-500" },
  { icon: Settings, label: "Configurações", href: "/configuracoes", color: "text-muted-foreground" },
];

export function MoreMenu({ open, onOpenChange }: MoreMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (href: string) => {
    triggerHaptic('light');
    onOpenChange(false);
    // Small delay for drawer animation
    setTimeout(() => {
      navigate(href);
    }, 150);
  };

  const handleLogout = async () => {
    triggerHaptic('medium');
    onOpenChange(false);
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="pb-2">
          <DrawerTitle className="text-center text-base font-semibold">
            Menu
          </DrawerTitle>
        </DrawerHeader>

        <div className="px-4 pb-8">
          <div className="grid grid-cols-4 gap-3">
            <AnimatePresence>
              {menuItems.map((item, index) => {
                const isActive = location.pathname === item.href ||
                  location.pathname.startsWith(item.href + '/');

                return (
                  <motion.button
                    key={item.href}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: { delay: index * 0.03 }
                    }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleNavigate(item.href)}
                    className={cn(
                      "flex flex-col items-center justify-center gap-2 p-3 rounded-2xl",
                      "transition-all duration-200",
                      "active:bg-muted/80",
                      isActive
                        ? "bg-primary/10 border border-primary/20"
                        : "bg-muted/40 hover:bg-muted/60"
                    )}
                  >
                    <div className={cn(
                      "w-11 h-11 rounded-xl flex items-center justify-center",
                      "bg-background shadow-sm",
                      isActive && "ring-2 ring-primary/20"
                    )}>
                      <item.icon className={cn("h-5 w-5", item.color)} />
                    </div>
                    <span className={cn(
                      "text-[11px] font-medium text-center leading-tight",
                      isActive ? "text-primary" : "text-muted-foreground"
                    )}>
                      {item.label}
                    </span>
                  </motion.button>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Logout Button */}
          <div className="mt-4 pt-4 border-t border-border/50">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-medium">Sair da conta</span>
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
